/**
 * Dynamic Zod schema builder
 * Converts FormSchema field definitions into a Zod validation schema for react-hook-form
 */

import { z } from "zod";
import type { FormField } from "@/types/agent-ui-types";

/**
 * Builds a Zod object schema from an array of FormField definitions
 * Each field gets a Zod validator matching its type and validation rules
 */
export function buildValidationSchema(
  fields: FormField[]
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    shape[field.id] = buildFieldSchema(field);
  }

  return z.object(shape);
}

/**
 * Builds a Zod schema for a single field based on its type and validation config
 */
function buildFieldSchema(field: FormField): z.ZodTypeAny {
  let schema: z.ZodTypeAny;

  switch (field.type) {
    case "text":
    case "textarea":
    case "email":
      schema = buildStringSchema(field);
      break;

    case "number":
      schema = buildNumberSchema(field);
      break;

    case "select":
    case "radio":
      schema = buildSelectSchema(field);
      break;

    case "checkbox":
      schema = buildCheckboxSchema(field);
      break;

    case "toggle":
      schema = z.boolean().default(false);
      break;

    case "file":
      schema = field.required
        ? z.instanceof(File, { message: "Please upload a file" })
        : z.instanceof(File).optional().or(z.literal(undefined));
      break;

    case "files":
      schema = field.required
        ? z.array(z.instanceof(File)).min(1, "Please upload at least one file")
        : z.array(z.instanceof(File)).default([]);
      break;

    case "date":
      schema = buildDateSchema(field);
      break;

    default:
      schema = z.string().optional();
  }

  return schema;
}

/** Builds string validation for text, textarea, and email fields */
function buildStringSchema(field: FormField): z.ZodTypeAny {
  let schema = z.string();

  if (field.type === "email") {
    schema = schema.email("Please enter a valid email address");
  }

  if (field.validation?.minLength) {
    schema = schema.min(
      field.validation.minLength,
      `Must be at least ${field.validation.minLength} characters`
    );
  }

  if (field.validation?.maxLength) {
    schema = schema.max(
      field.validation.maxLength,
      `Must be at most ${field.validation.maxLength} characters`
    );
  }

  if (field.validation?.pattern) {
    schema = schema.regex(
      new RegExp(field.validation.pattern),
      field.validation.patternMessage ?? "Invalid format"
    );
  }

  if (field.required) {
    return schema.min(1, `${field.label} is required`);
  }

  return schema.optional().or(z.literal(""));
}

/** Builds number validation */
function buildNumberSchema(field: FormField): z.ZodTypeAny {
  let schema = z.coerce.number();

  if (field.validation?.min !== undefined) {
    schema = schema.min(field.validation.min, `Must be at least ${field.validation.min}`);
  }

  if (field.validation?.max !== undefined) {
    schema = schema.max(field.validation.max, `Must be at most ${field.validation.max}`);
  }

  if (field.required) {
    return schema;
  }

  return schema.optional().or(z.literal("").transform(() => undefined));
}

/** Builds validation for single-choice select/radio */
function buildSelectSchema(field: FormField): z.ZodTypeAny {
  if (field.required) {
    return z.string().min(1, `Please select a ${field.label.toLowerCase()}`);
  }
  return z.string().optional().or(z.literal(""));
}

/** Builds validation for multi-choice checkbox */
function buildCheckboxSchema(field: FormField): z.ZodTypeAny {
  if (field.required) {
    return z.array(z.string()).min(1, "Please select at least one option");
  }
  return z.array(z.string()).default([]);
}

/** Builds date string validation */
function buildDateSchema(field: FormField): z.ZodTypeAny {
  if (field.required) {
    return z.string().min(1, `${field.label} is required`);
  }
  return z.string().optional().or(z.literal(""));
}

/**
 * Returns default values for a form based on field definitions
 * Used to initialize react-hook-form with sensible defaults
 */
export function getDefaultValues(
  fields: FormField[]
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};

  for (const field of fields) {
    switch (field.type) {
      case "toggle":
        defaults[field.id] = false;
        break;
      case "checkbox":
      case "files":
        defaults[field.id] = [];
        break;
      case "number":
        defaults[field.id] = "";
        break;
      default:
        defaults[field.id] = "";
    }
  }

  return defaults;
}
